// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { forwardRef } from 'react';
import { type FormatFn } from '@bloomberg/media.i18n';

import { labelIds } from '../i18n';
import { GlobalProps } from '../types';
import { createClasses } from '../utils';
import {
  isMoreRecentThan30Minutes,
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_HOUR,
  MILLISECONDS_PER_MINUTE,
  MILLISECONDS_PER_MONTH,
  MILLISECONDS_PER_YEAR,
} from '../utils/date';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './styles.module.css';

export enum RelativeDateFormat {
  SHORT,
  LONG,
}

export enum Source {
  ARTICLE,
  HUB,
}

export type RelativeDateProps = GlobalProps & {
  cutoffHours?: number;
  publishedAt?: string;
  updatedAt?: string;
  format?: RelativeDateFormat;
  locale?: string;
  showDateAfterCutoff?: boolean;
};

const DEFAULT_CUTOFF_HOURS = 4;
const MINUTE_TEXT = 'minute';
const HOUR_TEXT = 'hour';
const DAY_TEXT = 'day';
const MONTH_TEXT = 'month';
const YEAR_TEXT = 'year';

function constructTimeDifference(
  elapsed: number,
  format: RelativeDateFormat,
  locale: string,
  translateMessage: FormatFn<keyof typeof labelIds, string>
): string {
  // e.g. 1m ago
  const narrowFormatter = new Intl.RelativeTimeFormat(locale, {
    style: 'narrow',
  });
  // e.g. 1 minute ago
  const longFormatter = new Intl.RelativeTimeFormat(locale, {
    style: 'long',
  });

  if (elapsed < MILLISECONDS_PER_MINUTE) {
    return format === RelativeDateFormat.SHORT
      ? translateMessage({
          id: 'media-ui-core_lessThan1mAgo',
          defaultMessage: 'less than 1m ago',
        })
      : translateMessage({
          id: 'media-ui-core_lessThanAMinuteAgo',
          defaultMessage: 'less than a minute ago',
        });
  }

  if (elapsed < MILLISECONDS_PER_HOUR) {
    const elapsedInMinutes = Math.round(elapsed / MILLISECONDS_PER_MINUTE);

    return format === RelativeDateFormat.SHORT
      ? narrowFormatter.format(-elapsedInMinutes, MINUTE_TEXT)
      : longFormatter.format(-elapsedInMinutes, MINUTE_TEXT);
  }

  if (
    elapsed < MILLISECONDS_PER_HOUR * 2 &&
    format === RelativeDateFormat.LONG
  ) {
    return translateMessage({
      id: 'media-ui-core_anHourAgo',
      defaultMessage: 'an hour ago',
    });
  }

  if (elapsed < MILLISECONDS_PER_DAY) {
    const elapsedInHours = Math.round(elapsed / MILLISECONDS_PER_HOUR);

    return format === RelativeDateFormat.SHORT
      ? narrowFormatter.format(-elapsedInHours, HOUR_TEXT)
      : longFormatter.format(-elapsedInHours, HOUR_TEXT);
  }

  if (elapsed < MILLISECONDS_PER_MONTH) {
    const elapsedInDays = Math.round(elapsed / MILLISECONDS_PER_DAY);

    return format === RelativeDateFormat.SHORT
      ? translateMessage(
          {
            id: 'media-ui-core_approxDaysAgoShort',
            defaultMessage: `approx {daysElapsed}`,
          },
          {
            daysElapsed: narrowFormatter.format(-elapsedInDays, DAY_TEXT),
          }
        )
      : translateMessage(
          {
            id: 'media-ui-core_approxDaysAgoLong',
            defaultMessage: `approximately {daysElapsed}`,
          },
          {
            daysElapsed: longFormatter.format(-elapsedInDays, DAY_TEXT),
          }
        );
  }

  if (elapsed < MILLISECONDS_PER_YEAR) {
    const elapsedInMonths = Math.round(elapsed / MILLISECONDS_PER_MONTH);

    return format === RelativeDateFormat.SHORT
      ? translateMessage(
          {
            id: 'media-ui-core_approxMonthsAgoShort',
            defaultMessage: `approx {monthsElapsed}`,
          },
          {
            monthsElapsed: narrowFormatter.format(-elapsedInMonths, MONTH_TEXT),
          }
        )
      : translateMessage(
          {
            id: 'media-ui-core_approxMonthsAgoLong',
            defaultMessage: `approximately {monthsElapsed}`,
          },
          {
            monthsElapsed: longFormatter.format(-elapsedInMonths, MONTH_TEXT),
          }
        );
  }

  const elapsedInYears = Math.round(elapsed / MILLISECONDS_PER_YEAR);

  return format === RelativeDateFormat.SHORT
    ? translateMessage(
        {
          id: 'media-ui-core_approxYearsAgoShort',
          defaultMessage: `approx {yearsElapsed}`,
        },
        {
          yearsElapsed: narrowFormatter.format(-elapsedInYears, YEAR_TEXT),
        }
      )
    : translateMessage(
        {
          id: 'media-ui-core_approxYearsAgoLong',
          defaultMessage: `approximately {yearsElapsed}`,
        },
        {
          yearsElapsed: longFormatter.format(-elapsedInYears, YEAR_TEXT),
        }
      );
}

export function elapsedMins(date: Date): number {
  const now = Date.now();
  const elapsed = now - date.getTime();

  return elapsed / 60000;
}

export function relativeDate(
  date: Date,
  format: RelativeDateFormat,
  cutoffHours: number,
  translateMessage: FormatFn<keyof typeof labelIds, string>,
  showDateAfterCutoff?: boolean,
  locale: string = 'en-US'
): string {
  const now = Date.now();
  const elapsed = now - date.getTime();

  if (elapsed < cutoffHours * MILLISECONDS_PER_HOUR) {
    return constructTimeDifference(elapsed, format, locale, translateMessage);
  }

  if (showDateAfterCutoff) {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  return '';
}

export function dateStringIsValid(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  return !Number.isNaN(Date.parse(dateString));
}

export const RelativeDate = forwardRef<HTMLTimeElement, RelativeDateProps>(
  function RelativeDateComponent(props, ref) {
    const {
      cutoffHours = DEFAULT_CUTOFF_HOURS,
      publishedAt = '',
      updatedAt = '',
      format = RelativeDateFormat.SHORT,
      locale = 'en',
      showDateAfterCutoff = false,
    } = props;
    const translateMessage = useTranslateMessage();

    const validPublishedDate = dateStringIsValid(publishedAt);

    if (!validPublishedDate) {
      return null;
    }

    const publishedDate = new Date(publishedAt);

    const updatedDate = dateStringIsValid(updatedAt)
      ? new Date(updatedAt)
      : false;

    const updated =
      updatedDate && updatedDate.getTime() > publishedDate.getTime();
    const date = updated ? updatedDate : publishedDate;

    const relativeDateContent = relativeDate(
      date,
      format,
      cutoffHours,
      translateMessage,
      showDateAfterCutoff,
      locale
    );

    const dateContent =
      updated && relativeDateContent
        ? translateMessage(
            {
              id: 'media-ui-core_updatedTime',
              defaultMessage: 'updated {relativeDate}',
            },
            {
              relativeDate: relativeDateContent,
            }
          )
        : relativeDateContent;

    return (
      <time
        ref={ref}
        className={createClasses(
          styles.relativeDate,
          !updated && isMoreRecentThan30Minutes(date) ? 'recent' : ''
        )}
        dateTime={date.toISOString()}
        data-locale={locale}
        // Ignoring hydration mismatch here since server side content will be different from what is rendered in client.
        // Not rendering this also is an issue due to layout shift
        suppressHydrationWarning={true}
      >
        {dateContent}
      </time>
    );
  }
);
